import type { PartnerStatus } from "./types.ts";

const usd0 = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
	maximumFractionDigits: 0,
});
const usd2 = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
	maximumFractionDigits: 2,
});

export function fmtUsd(raw: number | string | null | undefined): string {
	if (raw === null || raw === undefined || raw === "") return "—";
	// pg NUMERIC arrives as a string through JSON.
	const v = typeof raw === "string" ? Number(raw) : raw;
	if (Number.isNaN(v)) return "—";
	if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
	if (Math.abs(v) >= 10_000) return usd0.format(v);
	return usd2.format(v);
}

export function fmtAmount(v: number | string | null | undefined, symbol?: string): string {
	if (v === null || v === undefined || v === "") return "—";
	const n = typeof v === "string" ? Number(v) : v;
	if (Number.isNaN(n)) return `${v}${symbol ? ` ${symbol}` : ""}`;
	const abs = Math.abs(n);
	const s =
		abs >= 1000
			? n.toLocaleString("en-US", { maximumFractionDigits: 2 })
			: abs >= 1
				? n.toLocaleString("en-US", { maximumFractionDigits: 4 })
				: n.toLocaleString("en-US", { maximumFractionDigits: 8 });
	return symbol ? `${s} ${symbol}` : s;
}

export function fmtNum(raw: number | string | null | undefined): string {
	if (raw === null || raw === undefined || raw === "") return "—";
	const v = typeof raw === "string" ? Number(raw) : raw;
	if (Number.isNaN(v)) return "—";
	return v.toLocaleString("en-US");
}

export function fmtDate(v: string | null | undefined, withTime = true): string {
	if (!v) return "—";
	const d = new Date(v);
	if (Number.isNaN(d.getTime())) return v;
	return withTime ? d.toLocaleString() : d.toLocaleDateString();
}

export function fmtAgo(v: string | null | undefined): string {
	if (!v) return "—";
	const t = new Date(v).getTime();
	if (Number.isNaN(t)) return v;
	const diff = Math.max(0, Date.now() - t);
	const s = Math.floor(diff / 1000);
	if (s < 60) return `${s}s ago`;
	const m = Math.floor(s / 60);
	if (m < 60) return `${m}m ago`;
	const h = Math.floor(m / 60);
	if (h < 48) return `${h}h ago`;
	const d = Math.floor(h / 24);
	if (d < 60) return `${d}d ago`;
	return new Date(v).toLocaleDateString();
}

export function fmtDuration(seconds: number | null | undefined): string {
	if (seconds === null || seconds === undefined) return "—";
	if (seconds < 60) return `${Math.round(seconds)}s`;
	if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
	return `${(seconds / 3600).toFixed(1)}h`;
}

export function shortAddr(a: string | null | undefined, head = 6, tail = 4): string {
	if (!a) return "—";
	if (a.length <= head + tail + 1) return a;
	return `${a.slice(0, head)}…${a.slice(-tail)}`;
}

/** Chain id → badge colours (bg/fg). Unknown chains fall back to neutral. */
const CHAIN_COLORS: Record<string, { bg: string; fg: string }> = {
	btc: { bg: "#f7931a22", fg: "#b45309" },
	bitcoin: { bg: "#f7931a22", fg: "#b45309" },
	eth: { bg: "#627eea22", fg: "#3b4fb8" },
	ethereum: { bg: "#627eea22", fg: "#3b4fb8" },
	tron: { bg: "#ff060a1a", fg: "#b91c1c" },
	trx: { bg: "#ff060a1a", fg: "#b91c1c" },
	bsc: { bg: "#f3ba2f22", fg: "#a16207" },
	bnb: { bg: "#f3ba2f22", fg: "#a16207" },
	polygon: { bg: "#8247e522", fg: "#6d28d9" },
	matic: { bg: "#8247e522", fg: "#6d28d9" },
	sol: { bg: "#14f19522", fg: "#047857" },
	solana: { bg: "#14f19522", fg: "#047857" },
	ltc: { bg: "#bfbbbb33", fg: "#4b5563" },
	arbitrum: { bg: "#28a0f022", fg: "#0369a1" },
	base: { bg: "#0052ff22", fg: "#1d4ed8" },
	avalanche: { bg: "#e8414222", fg: "#b91c1c" },
};

export function chainColor(chain: string): { bg: string; fg: string } {
	return CHAIN_COLORS[chain.toLowerCase()] ?? { bg: "#64748b22", fg: "#475569" };
}

export type Tone = "default" | "success" | "warning" | "error" | "info";

export function statusTone(status: PartnerStatus | string): Tone {
	switch (status) {
		case "active":
			return "success";
		case "paused":
			return "warning";
		case "terminated":
			return "warning";
		case "terminated_fraud":
			return "error";
		default:
			return "default";
	}
}

export function severityTone(sev: string | number | null | undefined): Tone {
	switch (String(sev ?? "").toLowerCase()) {
		case "critical":
		case "high":
			return "error";
		case "medium":
		case "warning":
			return "warning";
		case "low":
		case "info":
			return "info";
		default:
			return "default";
	}
}

export function jobStatusTone(status: string): Tone {
	switch (status) {
		case "done":
			return "success";
		case "running":
		case "queued":
			return "info";
		case "error":
			return "error";
		case "cancelled":
			return "warning";
		default:
			return "default";
	}
}

export function isJobActive(status: string): boolean {
	return status === "queued" || status === "running";
}

export function partnerStatusLabel(status: PartnerStatus | string): string {
	switch (status) {
		case "active":
			return "Active";
		case "paused":
			return "Paused";
		case "terminated":
			return "Terminated";
		case "terminated_fraud":
			return "Terminated (fraud)";
		default:
			return status;
	}
}
